'use client'

import { useEffect, useState } from 'react'
import { publishPlatDisplayName } from '@/lib/market/publish-plat-display-name'
import {
  MiddleDot,
  Price,
  TabularNumber,
} from '@/components/site/primitives'
import { cn } from '@/lib/utils'
import { trackEvent } from '@/lib/tracking'
import { listingCanonicalHref } from '@/lib/slug'
import { getCanonicalSiteUrl } from '@/lib/share-metadata'
import {
  daysLiveOnMarket,
  daysLiveSinceFirstListed,
  marketStartIncludesPriorListing,
  PRIOR_LISTING_DOM_NOTE,
  type ListingMarketStart,
} from '@/lib/listing/days-live'
import { isPublicOffMarketStatus } from '@/lib/listing-status-public'
import { redirectToLoginForSave } from '@/lib/pending-save'
import {
  addDeviceSavedListing,
  isDeviceSavedListing,
  removeDeviceSavedListing,
} from '@/lib/saved-device'
import { ListingGuestSaveSheet } from '@/components/site/listing-detail/ListingGuestSaveSheet.client'
import { ListingSaveButton, type ListingSaveState } from '@/components/site/listing-detail/ListingSaveButton'
import {
  ListingShareButton,
  ListingShareDialog,
} from '@/components/site/listing-detail/ListingShareButton'
import { useResumePendingSave } from '@/lib/hooks/useResumePendingSave'
import type { ListingDetail } from '@/lib/data/types/listing'
import { publishListingEstPayment } from '@/lib/listing/publish-listing-ask'
import { publishListingLeaseFigure } from '@/lib/listing/publish-lease-rate'
import {
  listingPublishesClosePrice,
  publishListingPublishedPrice,
  publishListingPublishedWholePropertyPrice,
} from '@/lib/listing/publish-listing-published-price'
import { publishListingHeroKeyStats } from '@/lib/listing/publish-listing-hero-stats'
import { publishListingShareKind, publishListingSharePricePerSqft } from '@/lib/listing/publish-listing-share'
import { listingContactHref, publishListingContactKey } from '@/lib/listing/publish-listing-contact-key'
import { publishStreetLine } from '@/lib/listing/publish-street-line'
import { publishListingLastDrop } from '@/lib/listing/publish-listing-history'
import { publishListingListedBy } from '@/lib/listing/publish-listing-listed-by'
import { formatPriceCompact } from '@/lib/format/money'
import {
  publishListingDropMark,
  type PublishedListingDropMark,
} from '@/lib/listing/publish-listing-drop-mark'
import type { PublishedListingPillRead } from '@/lib/listing/publish-listing-pill-read'
import { ActionSwapText } from '@/components/motion/action-swap'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { PriceDropMark } from './PriceDropMark'
import { ListingFoldActions } from './ListingFoldActions'
import { useAttributedContactBroker } from './use-attributed-broker'
import type { Broker } from '@/lib/data/types/broker'
import type { ListingNextStepLinks } from '@/lib/listing/listing-next-step'
import { closedPillLabel } from './closed-pill-label'

/**
 * PriceCtaStrip — price + address + pill row + CTA hierarchy under the hero.
 *
 * Hierarchy (E4 craft):
 *   1. Price (Layer A H1, address in sr-only + visible lines) — honest MLS numbers only
 *   2. Primary: Schedule a tour, the one filled ask beside the price, with
 *      Call and Text as their own outline buttons next to it (Matt
 *      2026-11-01: "i want seperate buttons / and should say schedule a
 *      tour"). Not joined into one bar, and not on a sticky bar.
 *   3. Secondary: Save / Share — always mounted (SITE-21 / SITE-99).
 *   4. Tertiary: Get alerts for homes like this → #listing-like-alerts
 *
 * Spec source:
 *   design_system/ryan-realty/ui_kits/listing-detail/index.html §ld-price-block
 *   design_system/ryan-realty/ui_kits/listing-detail/parity.json "PriceCtaStrip"
 */

type Props = {
  listing: Pick<
    ListingDetail,
    | 'listingKey'
    | 'listNumber'
    | 'onMarketDate'
    | 'listPrice'
    | 'closePrice'
    | 'closeDate'
    | 'status'
    | 'dom'
    | 'pricePerSqft'
    | 'propertySubType'
    | 'propertyType'
    | 'streetNumber'
    | 'streetDirPrefix'
    | 'streetName'
    | 'streetSuffix'
    | 'streetDirSuffix'
    | 'city'
    | 'postalCode'
    | 'subdivisionName'
    | 'originalListPrice'
    | 'priceDropCount'
    | 'beds'
    | 'baths'
    | 'sqft'
    | 'totalLivingAreaSqFt'
    | 'lotSizeAcres'
    | 'taxAnnualAmount'
    | 'hoaMonthly'
    | 'listAgentName'
    | 'listOfficeName'
    | 'lat'
    | 'lng'
  >
  /**
   * A commercial lease's "Lease Rate Options" unit (getLeaseRateOptions). On
   * an on-market MLS 'G' listing the strip prints the rent with this unit, or
   * "Lease rate not published", where a sale prints its ask, and a "For lease"
   * pill. Ignored on every other listing.
   */
  leaseRateOption?: string | null
  /** Published listing-history rail — last drop on the face comes from here. */
  history?: ReadonlyArray<{
    event?: string | null
    event_date?: string | null
    price?: number | null
    price_change?: number | null
  }>
  /**
   * The page's one start day (listingMarketStart, then the relist linker in
   * lib/listing/relist-link.ts). When given, the pill counts from it; a start
   * from an earlier MLS listing prints "including prior listing". Omitted,
   * the strip counts from OnMarketDate and `history` as before.
   */
  marketStart?: Pick<ListingMarketStart, 'day' | 'basis'> | null
  /** Save handler — caller wires to saved_listings table. Returns needsAuth=true
   *  for a signed-out visitor so the strip can route them to sign-in. */
  onSave?: (listingKey: string) => Promise<{ saved: boolean; needsAuth?: boolean }>
  /** Initial saved state, hydrated by the server. */
  initialSaved?: boolean
  /**
   * Guest save must open the Sheet without waiting on a server action
   * (SITE-99 demo match). Signed-in visitors still hit onSave.
   */
  signedIn?: boolean
  /** Share handler — optional extra after the Dialog opens. */
  onShare?: (listingKey: string) => void
  /** Override the default contact-tour href. */
  scheduleHref?: string
  /**
   * /book for this listing's broker. Mobile "Schedule a tour" uses this when
   * set, matching V3ListingClose. Falls back to the contact-tour href.
   */
  bookHref?: string
  /** Override the default ask-question href. */
  askHref?: string
  /**
   * Seed 30-yr rate in PERCENT, same prop the payment calculator receives.
   * Omit / null → DEFAULT_PITI_RATE inside computeMonthlyPiti.
   */
  ratePct?: number | null
  /**
   * Face Est. $/mo leftover. The house page keeps the one number in Payment
   * (computeMonthlyPiti). Tests still render the face label by default.
   */
  showEstPayment?: boolean
  showAlerts?: boolean
  /**
   * Where the off-market ask sends a reader who wants a home they can actually
   * buy: the active-inventory rail on this page. Required for the branch to
   * render its primary door.
   */
  similarHref?: string
  /** The saved-search capture on this page. The off-market secondary. */
  alertsHref?: string
  /**
   * SITE-45 / Matt 2026-09-15. Dated cut from publishListingDropMark.
   * Omit to derive from `history`. Pass null to force no mark (tests).
   */
  dropMark?: PublishedListingDropMark | null
  /**
   * SITE-45. One plain sentence under the status pills reading the day count
   * and the $/sqft against the place's record (publishListingPillRead), with
   * its trace behind a disclosure. Null prints the pills alone.
   */
  read?: PublishedListingPillRead | null
  /** True when this listing is Ryan Realty's. Another brokerage's phone stays off. */
  ours?: boolean
  /** P09 next step: the similar rail and the community market, under the fold actions. */
  nextStep?: ListingNextStepLinks
  /** P09 next step: the community slug every next-step event carries. */
  communitySlug?: string | null
  /**
   * The broker the Call and Text buttons ring: the page's contact broker,
   * swapped after hydration for this visitor's attributed broker (the same one
   * the broker card shows). Null builds no Call or Text button. Off market the
   * strip builds no `tel:` or `sms:` URI whatever this holds (SITE-21).
   */
  contactBroker?: { defaultBroker: Broker; brokers: Broker[]; lockToDefault?: boolean } | null
  className?: string
}

// KB pill registry. Brutalist navy-on-cream chips: a filled navy chip for
// the live/active states (the strongest visual weight), a cream chip with a
// 2px navy edge for everything else. `filled` flips the chip to navy ground.
const PILL_TONE: Record<string, { filled: boolean }> = {
  Active: { filled: true },
  'Active Under Contract': { filled: false },
  Pending: { filled: true },
  Closed: { filled: false },
  Withdrawn: { filled: false },
  Expired: { filled: false },
  Canceled: { filled: false },
}

export function PriceCtaStrip({
  listing,
  leaseRateOption = null,
  history,
  marketStart = null,
  onSave,
  initialSaved = false,
  signedIn = false,
  onShare,
  scheduleHref,
  bookHref,
  askHref,
  ratePct,
  showEstPayment = true,
  showAlerts = true,
  similarHref = '#similar',
  alertsHref = '#listing-like-alerts',
  dropMark,
  read = null,
  ours = false,
  nextStep,
  communitySlug = null,
  contactBroker = null,
  className,
}: Props) {
  // SITE-21. The four statuses under which nobody can buy this home. Read from
  // lib/listing-status-public.ts, which re-exports SITE-20's set — never from
  // the inverse of PUBLIC_ACTIVE_STATUSES, which would take the ask off every
  // Pending home on the site (a pending home takes backup offers and is a live
  // lead source).
  const offMarket = isPublicOffMarketStatus(listing.status)
  // See the pill below: one shared definition of days on market. It counts to
  // TODAY, which is a fact about a home that is still for sale and a fiction
  // about one that is not — an Expired 2022 listing read "1,153 days on
  // market". Off market, the day count is the sold instrument's, measured to
  // the close (lib/listing/days-live.ts daysOnMarketToClose), and this strip
  // publishes none. W6: the count starts at the earliest listing event in the
  // history this strip is handed (a relist resets OnMarketDate), the same start
  // the page's other day counts use (listingMarketStart).
  // Relist linker: the page hands its start day, which may come from an
  // earlier MLS listing of the same home; the pill then says so.
  const daysLive = offMarket
    ? null
    : marketStart
      ? daysLiveOnMarket(marketStart.day)
      : daysLiveSinceFirstListed(listing.onMarketDate ?? null, history)
  const daysIncludePrior = daysLive != null && marketStartIncludesPriorListing(marketStart)
  const [saveState, setSaveState] = useState<ListingSaveState>(initialSaved ? 'saved' : 'idle')
  const [guestSaveOpen, setGuestSaveOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  /** Signed-out save held on this device (lib/saved-device.ts, W12). */
  const [deviceSaved, setDeviceSaved] = useState(false)

  // Read in an effect, never during render (ci:hydration-safety).
  useEffect(() => {
    if (!signedIn) setDeviceSaved(isDeviceSavedListing(listing.listingKey))
  }, [signedIn, listing.listingKey])

  // RC7 resume: complete a save this listing was bounced to login for (the hook
  // owns the idempotent save + re-stash; this is the detail page's real save CTA).
  useResumePendingSave({
    listingKey: listing.listingKey,
    alreadySaved: saveState === 'saved',
    onSaved: () => setSaveState('saved'),
  })

  // SITE-20: this strip's hand-written `isClosed ? closePrice : listPrice` was
  // the ONLY status branch on the page, which is how the H1 came out right and
  // the meta description, the share card, the JSON-LD, the hero caption and the
  // map card all came out wrong. The branch now lives in one publisher every
  // one of those surfaces calls; the pill below still reads the raw status.
  const isClosed = listingPublishesClosePrice(listing.status)
  const headlinePrice = publishListingPublishedPrice({
    status: listing.status,
    listPrice: listing.listPrice,
    closePrice: listing.closePrice,
    propertyType: listing.propertyType,
  })
  const shareSubject = {
    propertySubType: listing.propertySubType,
    subdivisionName: listing.subdivisionName,
    city: listing.city,
    listNumber: listing.listNumber,
  }
  const shareKind = publishListingShareKind(shareSubject)
  // A commercial lease on the market: its ListPrice is rent, so the price slot
  // prints the rent with its unit (never a bare number) and the pills say
  // "For lease". Off market it prints nothing new: the rent a lease asked is
  // not a current offer, and the status pill already says what happened.
  const lease = offMarket
    ? null
    : publishListingLeaseFigure(
        { price: listing.listPrice, propertyType: listing.propertyType, leaseRateOption },
        'long',
      )
  // The alert this strip points at bands on the whole-home price, so the copy
  // may promise a price band only when one exists. 735 Purcell (a commercial
  // sublease publishing no price) and MLS 220190868 (a $1 fractional interest)
  // both read "…lists in this city near this price" over a band built from a
  // rent rate and a share.
  const alertBandIsPublished =
    publishListingPublishedWholePropertyPrice({
      ...shareSubject,
      status: listing.status,
      listPrice: listing.listPrice,
      closePrice: listing.closePrice,
      propertyType: listing.propertyType,
    }) != null
  const publishedPpsf = publishListingSharePricePerSqft({
    ...shareSubject,
    propertyType: listing.propertyType,
    pricePerSqft: listing.pricePerSqft,
  })
  const livingSqft = listing.sqft ?? listing.totalLivingAreaSqFt ?? null
  const factsLine = publishListingHeroKeyStats({
    beds: listing.beds,
    baths: listing.baths,
    sqft: livingSqft,
    acres: listing.lotSizeAcres,
  }).join(' · ')
  const contactKey = publishListingContactKey({
    listNumber: listing.listNumber,
    listingKey: listing.listingKey,
  })
  const street = publishStreetLine({
    streetNumber: listing.streetNumber,
    streetDirPrefix: listing.streetDirPrefix,
    streetName: listing.streetName,
    streetSuffix: listing.streetSuffix,
    streetDirSuffix: listing.streetDirSuffix,
  }) ?? ''
  const cityLine = [listing.city ? `${listing.city}, OR` : null, listing.postalCode]
    .filter(Boolean)
    .join(' ')
  const estPayment = showEstPayment
    ? publishListingEstPayment({
        listPrice: headlinePrice,
        taxAnnual: listing.taxAnnualAmount,
        hoaMonthly: listing.hoaMonthly,
        mortgageRate: ratePct,
      })?.label ?? null
    : null
  // A lease's history amounts are rent: no sale-price cut is drawn from them.
  const lastDrop = offMarket || lease || !history ? null : publishListingLastDrop(history)
  const newestDrop = lease ? null : dropMark !== undefined ? dropMark : publishListingDropMark(history)
  // The mark reads "Was $X · Cut $Y" under the headline price (Matt 2026-09-24,
  // one price since 2026-09-25), so it draws only while the cut set today's
  // price. publishListingDropMark returns the newest DROP and skips increases,
  // so after a later raise its `to` is no longer the headline price, and "Was"
  // beside it would describe a cut that did not produce it (§0).
  const datedDrop =
    newestDrop && headlinePrice != null && Math.round(headlinePrice) === newestDrop.to ? newestDrop : null
  const listedBy = publishListingListedBy({
    listAgentName: listing.listAgentName,
    listOfficeName: listing.listOfficeName,
    ours,
  })
  // MLS feeds mask private fields with `********` and stamp absent ones as
  // "N/A"; publishPlatDisplayName() collapses every such sentinel to null so the
  // address never renders "Bend, OR 97703 · N/A".
  const cleanSubdivision = publishPlatDisplayName(listing.subdivisionName)
  const cityWithCommunity = cleanSubdivision
    ? [cityLine, cleanSubdivision].filter(Boolean).join(' · ')
    : cityLine

  // Accessible names for the Save + Share controls. The visible labels are bare
  // verbs ("Save", "Share"), so a screen-reader visitor heard an action with no
  // object and no state. Name the property and reflect saved vs not, matching
  // components/listing/SaveListingButton.tsx ("Save to saved homes" /
  // "Remove from saved homes") with the address added.
  const propertyName = street || 'this home'
  // A guest's device save shows as Saved without touching `saveState`, which
  // the login resume reads as "already on the server" (W12).
  const shownSaveState: ListingSaveState = !signedIn && deviceSaved ? 'saved' : saveState
  const saveAriaLabel =
    shownSaveState === 'saved'
      ? `Remove ${propertyName} from your saved homes`
      : shownSaveState === 'saving'
        ? `Saving ${propertyName} to your saved homes`
        : `Save ${propertyName} to your saved homes`

  const tourHref =
    scheduleHref ?? listingContactHref(contactKey, 'tour') ?? `/contact?intent=tour`
  // Matt 2026-11-01: Call and Text are their own buttons beside Schedule a
  // tour. The broker's Twilio business line (phoneDirect): inbound is recorded,
  // logged to the CRM timeline and forwarded to their cell. SITE-21: no `tel:`
  // or `sms:` URI is built for a home that is not for sale.
  const phoneBroker = useAttributedContactBroker(
    contactBroker ? { ...contactBroker, assign: false } : null,
  )
  const phoneDigits = (phoneBroker?.phoneDirect ?? phoneBroker?.phoneFub ?? '').replace(/[^\d]/g, '')
  const callHref = offMarket ? null : phoneDigits ? `tel:${phoneDigits}` : null
  const textHref = offMarket ? null : phoneDigits ? `sms:${phoneDigits}` : null
  const askHrefResolved =
    askHref ?? listingContactHref(contactKey, 'question') ?? `/contact?intent=question`

  async function handleSave() {
    if (saveState === 'saving') return
    if (!signedIn) {
      // W12: a guest Save saves on this device at once; a second tap unsaves.
      // Email updates and Google sign-in are offered in the Sheet, not
      // required. The Sheet still opens synchronously (catalog demo: do not
      // wait on toggleSavedListing or the save-open shot records a closed
      // button).
      if (deviceSaved) {
        removeDeviceSavedListing(listing.listingKey)
        setDeviceSaved(false)
        return
      }
      setDeviceSaved(addDeviceSavedListing(listing.listingKey))
      setGuestSaveOpen(true)
      return
    }
    if (!onSave) return
    setSaveState('saving')
    try {
      const res = await onSave(listing.listingKey)
      if (res.needsAuth) {
        setSaveState('idle')
        setGuestSaveOpen(true)
        return
      }
      setSaveState(res.saved ? 'saved' : 'idle')
    } catch {
      setSaveState('idle')
    }
  }

  function handleShare() {
    setShareOpen(true)
    onShare?.(listing.listingKey)
  }

  const shareUrl = `${getCanonicalSiteUrl()}${listingCanonicalHref({
    listingKey: listing.listingKey,
    listNumber: listing.listNumber,
    streetNumber: listing.streetNumber,
    streetName: listing.streetName,
    city: listing.city,
    subdivisionName: listing.subdivisionName,
  })}`
  const shareTitle = street || `Listing ${listing.listNumber ?? listing.listingKey}`

  return (
    <div className={cn('listing-face', !offMarket && 'listing-face--has-fold', className)}>
      <div>
      <div className="listing-face__title">
      <h1 className="listing-ask">
        {street || `Listing ${listing.listNumber ?? listing.listingKey}`}
      </h1>
      <p className="listing-ask__price">
        {lease ? (
          <span className={cn('listing-ask__lease', !lease.rate && 'listing-ask__lease--none')}>
            {lease.text}
          </span>
        ) : (
          <Price value={headlinePrice} exact />
        )}
        {estPayment ? <span className="listing-ask__est">{estPayment}</span> : null}
      </p>
      {datedDrop && !offMarket ? (
        /* The cut at rest, with the date (Matt 2026-09-15), under the one
           headline price: "Was $X · Cut $Y (−Z%) on date" (Matt 2026-09-25). */
        <div className="listing-face__drop">
          <PriceDropMark
            mark={datedDrop}
            label={lastDrop?.label ?? `Price drop ${formatPriceCompact(datedDrop.drop)}`}
          />
        </div>
      ) : null}
      {factsLine ? (
        <div className="listing-face__facts">
          {factsLine}
        </div>
      ) : null}
      {cityWithCommunity ? (
        <div className="listing-face__place">
          {cityWithCommunity}
        </div>
      ) : null}
      </div>
      <div className="listing-face__price-row">
      <ButtonGroup aria-label="Save or share this listing" className="listing-face__keep gap-0">
        <ListingSaveButton saveState={shownSaveState} onSave={handleSave} ariaLabel={saveAriaLabel} />
        <ListingShareButton
          onShare={handleShare}
          ariaLabel={`Share ${propertyName}`}
        />
      </ButtonGroup>
      {/* SITE-21 + Matt 2026-11-01: Schedule a tour, Call and Text are three
          separate buttons beside the price, not one joined bar. Only Schedule
          a tour is filled (one filled ask). Off market, the doors a broker can
          still fulfil are homes for sale and alerts, and no call or text link
          is built. */}
      <div
        role="group"
        aria-label={offMarket ? 'Homes like this' : 'Schedule a tour or contact a broker'}
        className="listing-ask-row listing-face__ask"
      >
        {offMarket ? (
          <>
            <Button variant="outline" size="lg" asChild>
              <a href={similarHref}>
                <ActionSwapText value="homes">Homes for sale</ActionSwapText>
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href={alertsHref}>Get alerts</a>
            </Button>
          </>
        ) : (
          <>
            <Button size="lg" asChild>
              <a
                href={tourHref}
                onClick={() => trackEvent('schedule_tour_click', { listing_key: listing.listingKey, surface: 'listing_price_strip' })}
              >
                <ActionSwapText value="tour">Schedule a tour</ActionSwapText>
              </a>
            </Button>
            {callHref ? (
              <Button variant="outline" size="lg" asChild>
                <a
                  href={callHref}
                  onClick={() =>
                    trackEvent('click_cta', {
                      cta: 'listing_contact',
                      action: 'call',
                      listing_key: listing.listingKey,
                      surface: 'listing_price_strip',
                    })
                  }
                >
                  Call
                </a>
              </Button>
            ) : null}
            {textHref ? (
              <Button variant="outline" size="lg" asChild>
                <a
                  href={textHref}
                  onClick={() =>
                    trackEvent('click_cta', {
                      cta: 'listing_contact',
                      action: 'text',
                      listing_key: listing.listingKey,
                      surface: 'listing_price_strip',
                    })
                  }
                >
                  Text
                </a>
              </Button>
            ) : null}
          </>
        )}
      </div>
      </div>
      {!offMarket ? (
        <ListingFoldActions
          listingKey={listing.listingKey}
          addressLine={street}
          askHref={askHrefResolved}
          walkHref={bookHref ?? tourHref}
          callHref={callHref}
          textHref={textHref}
          kind={lease ? 'lease' : listing.propertyType === 'D' ? 'land' : 'home'}
          nextStep={nextStep}
          communitySlug={communitySlug}
        />
      ) : null}
      {listedBy ? (
        <div className="listing-face__listed">
          {listedBy}
        </div>
      ) : null}

      <div className="listing-face__pills no-scrollbar">
        <Pill kind={listing.status}>
          <span aria-hidden>●</span>{' '}
          {(isClosed ? closedPillLabel(listing.closeDate) : null) ?? listing.status}
        </Pill>
        {/* Days live from OnMarketDate, never `listing.dom` — that is the MLS
            DaysOnMarket field, which is list-to-close and is banned as DOM
            (CLAUDE.md §7). The close section counts the same way from the same
            helper, so the two figures on this page cannot disagree. */}
        {daysLive != null ? (
          <Pill kind="dom">
            <TabularNumber value={daysLive} /> {daysLive === 1 ? 'day' : 'days'} on market
            {daysIncludePrior ? `, ${PRIOR_LISTING_DOM_NOTE}` : null}
          </Pill>
        ) : null}
        {lease ? <Pill kind="dom">{lease.label}</Pill> : null}
        {shareKind ? <Pill kind="dom">{shareKind}</Pill> : null}
        {publishedPpsf != null ? (
          <Pill kind="psqft">
            <Price value={publishedPpsf} exact />/sqft
          </Pill>
        ) : null}
      </div>
      {read ? (
        /* The pills' plain read (SITE-45): what the day count and the $/sqft
           mean against the place's own record, from the same Market Truth
           reads the instrument further down makes. The trace sits behind a
           disclosure so the sentence stays a sentence. */
        <div className="listing-read">
          <p className="listing-read__sentence">{read.sentence}</p>
          <details className="listing-read__source">
            <summary>Source</summary>
            <p>{read.source}</p>
          </details>
        </div>
      ) : null}
      </div>
      {/* No Google raster of this house here. Matt 2026-09-02: a listing page
          carries the living map only, and a 118px roadmap thumbnail above it
          was a second visual language for the same house — plus a paid static
          map request per view (evaluator round five, LISTING-NOBOUNDARY-6). */}
      <div className="listing-face__actions">
      {showAlerts ? (
        <>
      <a
        href="#listing-like-alerts"
        className="btn mt-3 w-full text-center sm:w-auto"
        style={OUTLINE_BTN_STYLE}
        aria-label="Get free email alerts for homes like this"
      >
        Get free alerts for homes like this <span className="arr">→</span>
      </a>
      <p className="mt-2 text-xs" style={{ color: 'color-mix(in srgb, var(--v3-navy) 72%, transparent)' }}>
        {alertBandIsPublished
          ? 'Free email when a new home lists in this city near this price. Unsubscribe any time.'
          : 'Free email when a new home lists in this city. Unsubscribe any time.'}
      </p>
        </>
      ) : null}
      </div>
      <ListingShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={shareUrl}
        shareTitle={shareTitle}
      />
      <ListingGuestSaveSheet
        listingKey={listing.listingKey}
        addressLine={street || null}
        savedOnDevice={deviceSaved}
        open={guestSaveOpen && saveState !== 'saved'}
        onOpenChange={setGuestSaveOpen}
        onUseGoogle={() => redirectToLoginForSave(listing.listingKey)} // hydration-safe: click callback, never runs during render
        onDone={() => {
          setGuestSaveOpen(false)
          setSaveState('saved')
        }}
      />
    </div>
  )
}

// Outlined-on-cream variant of the KB .btn. The base .btn ships a cream
// ground + cream edge (built for navy surfaces); on this cream strip we flip
// it to a transparent ground with a 2px navy edge + navy text so the
// secondary actions read as outlined brutalist chips next to the navy-filled
// primary (.btn.alt).
const OUTLINE_BTN_STYLE: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--navy)',
  borderColor: 'var(--navy)',
  minHeight: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
}

function Pill({
  kind,
  children,
}: {
  kind: keyof typeof PILL_TONE | 'dom' | 'psqft'
  children: React.ReactNode
}) {
  // dom + psqft are always quiet cream chips; status chips can flip to a
  // filled navy ground for the live/active states.
  const filled = kind === 'dom' || kind === 'psqft' ? false : (PILL_TONE[kind]?.filled ?? false)
  const navy = 'var(--navy)'
  const cream = 'var(--cream)'
  return (
    <span
      className="mono-lab"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: `2px solid ${navy}`,
        background: filled ? navy : 'transparent',
        color: filled ? cream : navy,
        padding: '5px 11px',
        fontSize: '0.62rem',
        letterSpacing: '0.14em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
      }}
    >
      {children}
    </span>
  )
}

// Silence unused import warning — MiddleDot is part of the v2 type
// surface used inside the subdivision separator (we currently use a
// plain ' · ' string for the city line; keeping the import for the
// future when we move to <MiddleDot /> for tabular precision).
void MiddleDot
